import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IoMapOutline, IoSettingsOutline } from 'react-icons/io5';

const ModesSection = styled.section`
  padding: 5rem 2rem;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionLabel = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: ${({ theme }) => theme.colors.activeColor}20;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  border-radius: 50px;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.headline};
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.activeColor};
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.paragraph};
  max-width: 650px;
  margin: 0 auto;
  line-height: 1.7;
`;

const ModesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ModeCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground || theme.colors.background};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: 2.5rem;
  border: 2px solid ${({ $highlighted, theme }) =>
    $highlighted ? theme.colors.activeColor : (theme.colors.border || 'rgba(0,0,0,0.1)')};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  position: relative;
  overflow: hidden;

  opacity: 0;
  transform: scale(0.95);
  transition: opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s;

  &.visible {
    opacity: 1;
    transform: scale(1);
  }

  &:nth-child(1) { transition-delay: 0s; }
  &:nth-child(2) { transition-delay: 0.15s; }

  ${({ $highlighted, theme }) => $highlighted && `
    &::before {
      content: 'Most Popular';
      position: absolute;
      top: 1rem;
      right: -2rem;
      background: ${theme.colors.activeColor};
      color: white;
      padding: 0.25rem 2.5rem;
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
      transform: rotate(45deg);
    }
  `}
`;

const ModeIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.activeColor}15;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.activeColor};
`;

const ModeTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.headline};
  margin-bottom: 1rem;
`;

const ModeDescription = styled.p`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.paragraph};
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.paragraph};
  margin-bottom: 0.75rem;
  line-height: 1.5;

  &::before {
    content: '\u2713';
    color: ${({ theme }) => theme.colors.activeColor};
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const modes = [
  {
    icon: <IoMapOutline size={28} />,
    title: 'ZIP Code Mode',
    description: 'Set it and forget it. Add ZIP codes to your watchlist and we analyze every new listing automatically.',
    highlighted: true,
    features: [
      'Live MLS listings pulled daily',
      'Auto-analyzed for cash flow',
      'COC%, IRR, and valuation for every passing property',
      'Track multiple markets at once',
      'Never miss a deal in your target areas'
    ]
  },
  {
    icon: <IoSettingsOutline size={28} />,
    title: 'Manual Mode',
    description: 'Deep-dive a specific property. Enter the details yourself and customize every assumption.',
    highlighted: false,
    features: [
      'Full control over all inputs',
      'Set component ages for precise CapEx',
      'Analyze off-market deals',
      'Test multiple scenarios quickly',
      'Perfect for due diligence on a deal'
    ]
  }
];

const TwoModes = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const fallback = setTimeout(() => setIsVisible(true), 3000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <ModesSection ref={sectionRef}>
      <Container>
        <SectionHeader className={isVisible ? 'visible' : ''}>
          <SectionLabel>Two Ways to Analyze</SectionLabel>
          <SectionTitle>
            Scan Markets or <AccentText>Deep-Dive</AccentText> One Property
          </SectionTitle>
          <Subtitle>
            Whether you want to scan entire markets or deep-dive a single property, we've got you covered.
          </Subtitle>
        </SectionHeader>
        <ModesGrid>
          {modes.map((mode) => (
            <ModeCard
              key={mode.title}
              $highlighted={mode.highlighted}
              className={isVisible ? 'visible' : ''}
            >
              <ModeIcon>{mode.icon}</ModeIcon>
              <ModeTitle>{mode.title}</ModeTitle>
              <ModeDescription>{mode.description}</ModeDescription>
              <FeatureList>
                {mode.features.map((feature) => (
                  <FeatureItem key={feature}>{feature}</FeatureItem>
                ))}
              </FeatureList>
            </ModeCard>
          ))}
        </ModesGrid>
      </Container>
    </ModesSection>
  );
};

export default TwoModes;
